import { useState } from 'react';
import { useGameState } from '../../net/GameProvider.js';
import { useI18n } from '../../i18n.js';
import { getUserAvatar } from '../../utils/avatar.js';
import { CardUI } from '../CardUI.js';
import { Icon } from '../Icon.js';
import { ReactionBubble } from '../ReactionUI.js';
import { useTableInteractions } from './TableInteractions.js';
import { PlayerProfileModal } from '../profile/PlayerProfileModal.js';

export function Opponents() {
  const { state, actions } = useGameState();
  const { lang, t } = useI18n();
  const isMn = lang === 'mn';
  const { game, reactions } = state;
  const [pendingFriendIds, setPendingFriendIds] = useState<Set<string>>(new Set());
  const [inspectedOpponent, setInspectedOpponent] = useState<{
    userId: string;
    name: string;
    tablePlayer: { playerId: string; name: string; seat: number; tier?: string };
  } | null>(null);

  let openPicker: ((target?: any) => void) | null = null;
  try {
    const ti = useTableInteractions();
    openPicker = ti.openPicker;
  } catch {
    // optional outside of TableInteractionsProvider
  }

  if (!game) return null;

  const isMuushig = game.gameType === 'muushig' && !!game.muushig;
  const muushig = game.muushig ?? null;
  const isTwentyOne = game.gameType === 'twentyone' && !!game.twentyone;
  const twentyone = game.twentyone ?? null;

  const myIndex = game.players.findIndex((p) => p.playerId === game.myPlayerId);
  const getOpponent = (offset: number) => {
    if (game.players.length < 2 && offset > 0) return null;
    const idx = (myIndex + offset) % game.players.length;
    return game.players[idx];
  };

  const leftOpponent = game.players.length >= 3 ? getOpponent(1) : null;
  const topOpponent =
    game.players.length === 2
      ? getOpponent(1)
      : game.players.length === 4
      ? getOpponent(2)
      : null;
  const topLeftOpponent = game.players.length === 5 ? getOpponent(2) : null;
  const topRightOpponent = game.players.length === 5 ? getOpponent(3) : null;
  const rightOpponent =
    game.players.length === 5
      ? getOpponent(4)
      : game.players.length === 4
      ? getOpponent(3)
      : game.players.length === 3
      ? getOpponent(2)
      : null;

  const getOpponentReactionStyle = (pos: 'top' | 'left' | 'right' | 'top-left' | 'top-right'): React.CSSProperties => {
    switch (pos) {
      case 'left':
        // Towards table center: right of the badge
        return {
          left: '100%',
          top: '50%',
          transform: 'translate(10px, -50%)',
        };
      case 'right':
        // Towards table center: left of the badge
        return {
          right: '100%',
          top: '50%',
          transform: 'translate(-10px, -50%)',
        };
      case 'top-left':
        // Towards table center: below and angled towards center
        return {
          top: '100%',
          left: '50%',
          transform: 'translate(20px, 8px)',
        };
      case 'top-right':
        // Towards table center: below and angled towards center
        return {
          top: '100%',
          right: '50%',
          transform: 'translate(-20px, 8px)',
        };
      case 'top':
      default:
        // Towards table center: below the top badge
        return {
          top: '100%',
          left: '50%',
          transform: 'translate(-50%, 8px)',
        };
    }
  };

  const renderPlayerBadge = (p: (typeof game.players)[0] | null, position: 'top' | 'left' | 'right' | 'top-left' | 'top-right') => {
    if (!p) return null;
    const isActive = game.activePlayerId === p.playerId;
    const lastAction = game.lastActions[p.playerId];
    const activeReaction = reactions[p.playerId];
    const pTotal = game.totals?.[p.playerId] ?? (isMuushig ? 15 : 0);
    const visibleCards = p.folded ? 0 : p.cardCount;
    const avatar = getUserAvatar(p.playerId, p.name);
    
    // Calculate bounded fan spread
    const maxArc = 48; // max total rotation in degrees
    const maxSpread = 66; // max total horizontal spread in px
    const angleStep = visibleCards > 1 ? Math.min(6, maxArc / (visibleCards - 1)) : 0;
    const xStep = visibleCards > 1 ? Math.min(8.5, maxSpread / (visibleCards - 1)) : 0;

    const isVip = p.tier === 'paid';
    const myUserId = state.profile?.id;
    const isRegisteredHuman = !p.isBot && !p.botControlled && !!p.userId;
    const isFriend = Boolean(myUserId && p.userId && state.friendsData?.friends.some((f) => f.userId === p.userId));
    const isPending = Boolean(myUserId && p.userId && state.friendsData?.outgoingRequests.some((r) => r.toUserId === p.userId));
    const totalCards = game.format.cardsEach;
    const handOrderIndex = isMuushig && muushig ? (p.seat - muushig.dealerSeat + game.players.length) % game.players.length : null;
    const isLowCount = !p.folded && p.cardCount > 0 && p.cardCount <= 3;
    const isCriticalCount = !p.folded && p.cardCount === 1;

    const handlePlayerClick = () => {
      if (p.userId) {
        setInspectedOpponent({
          userId: p.userId,
          name: p.name,
          tablePlayer: { playerId: p.playerId, name: p.name, seat: p.seat, tier: p.tier },
        });
      } else if (openPicker) {
        openPicker({ playerId: p.playerId, name: p.name, seat: p.seat, tier: p.tier });
      }
    };

    return (
      <div
        className={`opponent-badge opponent-badge-${position}${isActive ? ' is-active' : ''}${p.folded ? ' is-folded' : ''}`}
        data-seat={p.seat}
      >
        {/* Active Turn Pill */}
        {isActive && (
          <div className="opponent-turn-indicator" aria-label={t.waitingForTurn(p.name)}>
            <span className="opponent-turn-pulsar" />
            <span>{t.turnActive}</span>
          </div>
        )}

        {/* Pass Pill */}
        {!isActive && lastAction?.type === 'pass' && (
          <div className="opponent-pass-indicator" aria-label={isMn ? 'Өнжсөн' : 'Passed'}>
            <Icon name="skip-forward" size={10} />
            <span>{isMn ? 'Өнжсөн' : 'Pass'}</span>
          </div>
        )}

        <div
          className={`seat-disc${isVip ? ' is-vip' : ''}`}
          onClick={handlePlayerClick}
          title={
            isTwentyOne
              ? `${p.name} · ${pTotal} ${isMn ? 'чип' : 'chips'}`
              : isMuushig
              ? `${p.name} · ${pTotal} ${isMn ? 'оноо' : 'points'}`
              : game.pointLimit
              ? `${p.name} · ${pTotal} / ${game.pointLimit} ${isMn ? 'оноо' : 'pts'}`
              : p.name
          }
          style={{
            cursor: p.userId || openPicker ? 'pointer' : 'default',
            background: avatar.gradient,
            borderColor: isVip ? undefined : (isActive ? '#34d399' : avatar.borderColor),
            boxShadow: isVip
              ? undefined
              : isActive
              ? `0 0 0 3px #34d399, 0 0 16px rgba(52, 211, 153, 0.65), inset 0 1px 2px rgba(255, 255, 255, 0.35)`
              : `0 0 12px ${avatar.glow}, inset 0 1px 2px rgba(255, 255, 255, 0.25)`,
            color: '#ffffff',
            fontWeight: 800,
            textShadow: '0 1px 3px rgba(0, 0, 0, 0.65)',
            ...(game.pointLimit && pTotal >= game.pointLimit * 0.8 && !isActive
              ? { borderColor: '#f87171', color: '#fca5a5', boxShadow: '0 0 14px rgba(239, 68, 68, 0.55)' }
              : undefined),
          }}
        >
          {avatar.initial}
          {muushig?.dealerSeat === p.seat && <span className="dealer-marker" title={isMn ? 'Тараагч' : 'Dealer'}>D</span>}
          {twentyone?.bankerSeat === p.seat && <span className="dealer-marker" title={isMn ? 'Банкир' : 'Banker'}>B</span>}
          {p.abandoned ? (
            <span className="seat-state is-abandoned" title={t.abandoned}>🛡️</span>
          ) : p.botControlled || (p.isBot && !p.name.startsWith('Bot ') && !p.name.startsWith('Бот ')) ? (
            <span className="seat-state is-bot-takeover" title={t.botTakeover}>🤖</span>
          ) : !p.connected ? (
            <span className="offline-dot" title={t.offline} />
          ) : null}
          {isTwentyOne && twentyone?.golden21PlayerIds.includes(p.playerId) && (
            <span className="seat-state" style={{ color: '#fbbf24' }} title={isMn ? 'Алтан 21' : 'Golden 21'}>✨</span>
          )}
          {isTwentyOne && twentyone?.bustedPlayerIds.includes(p.playerId) && (
            <span className="seat-state" style={{ color: '#f87171' }} title={isMn ? 'Шатсан' : 'Busted'}>💥</span>
          )}
          {isTwentyOne && twentyone?.stoodPlayerIds.includes(p.playerId) && !twentyone.golden21PlayerIds.includes(p.playerId) && (
            <span className="seat-state" style={{ color: '#94a3b8' }} title={isMn ? 'Зогссон' : 'Stood'}>✋</span>
          )}
          {(p.folded || (!isTwentyOne && !isMuushig && lastAction?.type === 'pass')) && (
            <span className="seat-state" title={isMn ? 'Өнжсөн' : 'Passed'}><Icon name="skip-forward" size={12} /></span>
          )}
          {isMuushig && muushig?.stage === 'decision' && p.decided && !p.folded && (
            <span className="seat-state is-stayed" style={{ background: '#059669', color: '#fff', borderColor: '#34d399' }} title={isMn ? 'Орсон (Тоглоно)' : 'Staying (Playing)'}><Icon name="check" size={12} /></span>
          )}
          {isMuushig && muushig?.stage === 'exchange' && p.exchanged && !p.folded && (
            <span className="seat-state is-stayed" style={{ background: '#0284c7', color: '#fff', borderColor: '#38bdf8' }} title={isMn ? 'Сольсон' : 'Exchanged'}><Icon name="check" size={12} /></span>
          )}

          {/* Action button on other player avatar (opponents only) */}
          {openPicker && (
            <button
              type="button"
              className="seat-throw-action-btn"
              onClick={(e) => {
                e.stopPropagation();
                openPicker({ playerId: p.playerId, name: p.name, seat: p.seat, tier: p.tier });
              }}
              title={isMn ? `${p.name} рүү шидэх / тавлах` : `Throw item or taunt ${p.name}`}
              aria-label={isMn ? `${p.name} рүү шидэх / тавлах` : `Throw item or taunt ${p.name}`}
            >
              <Icon name="crosshair" size={13} className="seat-throw-icon" />
            </button>
          )}
        </div>

        {/* Opponent name right below avatar */}
        <span
          className={`opponent-name${isActive ? ' is-active' : ''}`}
          title={p.name}
          onClick={handlePlayerClick}
          style={{ cursor: p.userId || openPicker ? 'pointer' : 'default' }}
        >
          {p.name}
          {typeof p.level === 'number' && p.level > 0 && (
            <span style={{ fontSize: '0.8em', opacity: 0.85, marginLeft: 4, color: '#38bdf8' }}>
              Lv.{p.level}
            </span>
          )}
        </span>

        {/* Departure / connection status pill */}
        {p.abandoned ? (
          <span className="opponent-status-pill is-abandoned" title={t.abandoned}>
            🛡️ {t.abandoned}
          </span>
        ) : p.botControlled || (p.isBot && !p.name.startsWith('Bot ') && !p.name.startsWith('Бот ')) ? (
          <span className="opponent-status-pill is-bot" title={t.botTakeover}>
            🤖 {t.botTakeover}
          </span>
        ) : !p.connected ? (
          <span className="opponent-status-pill is-offline" title={t.offline}>
            🔌 {t.offline}
          </span>
        ) : null}

        {/* Friend Action: Add Friend if registered human, current user is signed in, and not already friends */}
        {isRegisteredHuman && p.userId && myUserId && (
          <div className="opponent-friend-slot">
            {isFriend ? (
              <span className="opponent-friend-tag is-friend" title={isMn ? 'Таны найз' : 'Friend'}>
                ✓ {isMn ? 'Найз' : 'Friend'}
              </span>
            ) : isPending || pendingFriendIds.has(p.userId) ? (
              <span className="opponent-friend-tag is-pending" title={isMn ? 'Хүсэлт илгээсэн' : 'Request pending'}>
                ⌛ {isMn ? 'Хүлээгдэж буй' : 'Pending'}
              </span>
            ) : p.userId !== myUserId ? (
              <button
                type="button"
                className="opponent-add-friend-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  if (p.userId) {
                    setPendingFriendIds((prev) => new Set(prev).add(p.userId!));
                    actions.sendFriendRequest({ toUserId: p.userId });
                  }
                }}
                title={isMn ? `${p.name}-г найзаар нэмэх` : `Add ${p.name} as friend`}
              >
                + {isMn ? 'Найз' : 'Add'}
              </button>
            ) : null}
          </div>
        )}

        {/* Muushig Dealer badge only (avoid cluttering all opponents with 1-r gar, 2-r gar, etc.) */}
        {handOrderIndex === 0 && (
          <span
            className="hand-order-badge is-dealer"
            title={t.dealerRole}
          >
            👑 {t.dealerRole}
          </span>
        )}

        <div className={`opponent-hand${isActive ? ' is-active' : ''}`} aria-hidden="true">
          {Array.from({ length: visibleCards }, (_, index) => {
            const offset = index - (visibleCards - 1) / 2;
            return (
              <div
                key={index}
                className={`opponent-hand-card${isActive ? ' is-turn-active' : ''}${isCriticalCount ? ' is-critical' : isLowCount ? ' is-warning' : ''}`}
                style={{ transform: `translateX(${offset * xStep}px) rotate(${offset * angleStep}deg)` }}
              >
                <CardUI faceDown />
              </div>
            );
          })}
        </div>

        <div className="seat-counters">
          {!isMuushig && (
            !isTwentyOne && (
              <>
                <span
                  className={`seat-card-ratio${isCriticalCount ? ' is-critical' : isLowCount ? ' is-warning' : ''}`}
                  title={`${p.cardCount} / ${totalCards} ${isMn ? 'хөзөр' : 'cards'}`}
                  aria-label={`${p.cardCount} / ${totalCards} ${isMn ? 'хөзөр' : 'cards'}`}
                >
                  <Icon name="cards" size={13} />
                  <strong>{p.cardCount}</strong>
                  <span className="card-ratio-total">/{totalCards}</span>
                </span>
                {game.pointLimit ? (
                  <span
                    className={`seat-point-limit${pTotal >= game.pointLimit * 0.8 ? ' is-danger' : pTotal >= game.pointLimit * 0.6 ? ' is-warning' : ''}`}
                    title={`${isMn ? 'Оноо / Хязгаар' : 'Points / Limit'}: ${pTotal} / ${game.pointLimit}`}
                    aria-label={`${isMn ? 'Оноо / Хязгаар' : 'Points / Limit'}: ${pTotal} / ${game.pointLimit}`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 3,
                      padding: '2px 7px',
                      borderRadius: 999,
                      background: pTotal >= game.pointLimit * 0.8 ? 'rgba(239, 68, 68, 0.28)' : 'rgba(255, 255, 255, 0.12)',
                      border: `1px solid ${pTotal >= game.pointLimit * 0.8 ? 'rgba(239, 68, 68, 0.55)' : 'rgba(255, 255, 255, 0.22)'}`,
                      fontWeight: 800,
                      color: pTotal >= game.pointLimit * 0.8 ? '#fca5a5' : pTotal >= game.pointLimit * 0.6 ? '#fde047' : '#f1f5f9',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Icon name="star" size={11} className="seat-point-icon" />
                    <span>{pTotal}/{game.pointLimit}</span>
                  </span>
                ) : null}
              </>
            )
          )}
          {isTwentyOne && twentyone?.bets[p.playerId] !== undefined && twentyone.bets[p.playerId] > 0 && (
            <span title={isMn ? 'Бооцоо' : 'Bet'} style={{ color: '#fef08a', display: 'inline-flex', alignItems: 'center', gap: 2 }}>
              🪙 {twentyone.bets[p.playerId]}
            </span>
          )}
          {isTwentyOne && (
            <span title={isMn ? 'Чип' : 'Chips'} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
              💰 {pTotal}
            </span>
          )}
          {isMuushig && (
            <>
              <span
                className="seat-muushig-points"
                title={isMn ? `Оноо: ${pTotal} (0 болж хожно)` : `Score: ${pTotal} (Target: 0)`}
                aria-label={`${isMn ? 'Оноо' : 'Points'}: ${pTotal}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 3,
                  padding: '2px 7px',
                  borderRadius: 999,
                  background: 'rgba(251, 191, 36, 0.22)',
                  border: '1px solid rgba(251, 191, 36, 0.45)',
                  fontWeight: 800,
                  color: '#fef08a',
                  whiteSpace: 'nowrap',
                }}
              >
                <Icon name="star" size={11} className="seat-point-icon" />
                <span>{pTotal}</span>
              </span>
              {/* Muushig Tricks - only rendered during active trick-taking stage */}
              {muushig?.stage === 'playing' && (
                p.folded ? (
                  <span
                    className="seat-muushig-tricks is-folded"
                    title={isMn ? 'Энэ өрөгт өнжсөн' : 'Sitting out'}
                    style={{ color: '#94a3b8', fontWeight: 600 }}
                  >
                    {isMn ? 'Өнжсөн' : 'Fold'}
                  </span>
                ) : (p.tricksWon ?? 0) === 0 ? (
                  <span
                    className="seat-muushig-tricks is-danger"
                    title={isMn ? '0 гэр: +5 торгуулийн аюултай!' : '0 tricks: risk of +5 Muushig penalty!'}
                    aria-label={`0 ${isMn ? 'гэр' : 'tricks'}`}
                  >
                    <span className="trick-danger-icon" aria-hidden="true">⚠️</span>
                    <strong className="trick-count">0</strong>
                    <span className="trick-unit">{isMn ? 'гэр' : 'tk'}</span>
                  </span>
                ) : (
                  <span
                    className={`seat-muushig-tricks${(p.tricksWon ?? 0) > 0 ? ' is-safe' : ''}`}
                    title={isMn ? `Авсан гэр: ${p.tricksWon ?? 0}` : `Tricks won: ${p.tricksWon ?? 0}`}
                    aria-label={`${p.tricksWon ?? 0} ${isMn ? 'гэр' : 'tricks'}`}
                  >
                    <Icon name="card-pile" size={13} className="seat-trick-icon" />
                    <strong className="trick-count">{p.tricksWon ?? 0}</strong>
                    <span className="trick-unit">{isMn ? 'гэр' : 'tk'}</span>
                  </span>
                )
              )}
            </>
          )}
          {state.voiceEnabled && (() => {
            const vp = state.voiceParticipants.find((v) => v.playerId === p.playerId);
            if (!vp) return null;
            return (
              <span
                title={vp.muted ? (isMn ? 'Мик хаалттай' : 'Mic muted') : (isMn ? 'Дуут ярианд холбогдсон' : 'In voice chat')}
                style={{ color: vp.muted ? '#fca5a5' : '#86efac' }}
              >
                <Icon name={vp.muted ? 'mic-off' : 'mic'} size={12} />
              </span>
            );
          })()}
        </div>

        {/* Opponent Floating Reaction Bubble (anchored towards table center) */}
        {activeReaction && (
          <div
            className={`opponent-reaction-slot opponent-reaction-${position}`}
            style={getOpponentReactionStyle(position)}
          >
            <ReactionBubble
              reactionId={activeReaction.reactionId}
              seq={activeReaction.seq}
              playerName={p.name}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="opponents-container">
        {renderPlayerBadge(leftOpponent, 'left')}
        {renderPlayerBadge(topOpponent, 'top')}
        {renderPlayerBadge(topLeftOpponent, 'top-left')}
        {renderPlayerBadge(topRightOpponent, 'top-right')}
        {renderPlayerBadge(rightOpponent, 'right')}
      </div>

      {inspectedOpponent && (
        <PlayerProfileModal
          userId={inspectedOpponent.userId}
          initialPlayerName={inspectedOpponent.name}
          tableInteractionPlayer={inspectedOpponent.tablePlayer}
          onOpenTableInteractions={openPicker ?? undefined}
          onClose={() => setInspectedOpponent(null)}
        />
      )}
    </>
  );
}
